import type { Step } from 'react-joyride';

import {
  geofenceAttention,
  geofenceCircle,
  geofenceCircleEdit,
  geofenceDrag,
  geofencePolygon,
  geofencePolygonEdit,
  geofenceRetangle,
} from './assets';

import { colors } from '@cobliteam/base-components/src/styles/colors';

import { ContentTour, TitleTour } from '../../../components/Tour';

const commonConfig = {
  spotlightPadding: 10,
  disableBeacon: true,
  styles: {
    spotlight: {
      border: `2px solid ${colors.primary.default}`,
      borderRadius: '10px',
    },
  },
};

const steps: Step[] = [
  {
    ...commonConfig,
    target: '#geofenceTour1',
    placement: 'left-start',
    title: (
      <TitleTour
        title='Para começar, vamos definir um nome.'
        description='Esse nome será utilizado para buscas, eventos e relatórios. Então sugerimos que você defina um nome que seja fácil de encontrar.'
      />
    ),
    content: <ContentTour />,
  },
  {
    ...commonConfig,
    target: '#geofenceTour2',
    placement: 'left-start',
    title: (
      <TitleTour
        title='A busca de endereço é opcional mas te ajuda a encontrar o local no mapa.'
        description='No campo ao lado "Buscar endereço", digite um endereço que seja próximo a sua cerca. Essa ação fará o mapa dar zoom até a área a ser delimitada.'
      />
    ),
    content: <ContentTour />,
  },
  {
    ...commonConfig,
    target: '#geofenceTour3',
    placement: 'left-start',
    title: (
      <TitleTour
        title='Escolha qual será o formato da sua cerca.'
        description='Disponibilizamos 3 formatos possíveis: Circular, retangular ou poligonal. Detalharemos um a um nas próximas telas.'
      />
    ),
    content: <ContentTour />,
  },
  {
    ...commonConfig,
    target: '#geofenceTour4 > button:nth-child(1)',
    placement: 'left-start',
    title: (
      <TitleTour
        title='Circular: Defina o centro da sua cerca e arraste para definir o tamanho.'
        description='O circulo é desenhado a partir do centro, então clique primeiro no ponto que será o centro da sua cerca e arraste até alcançar o tamanho desejado. Clique novamente no mapa para concluir o desenho.'
      />
    ),
    content: (
      <ContentTour image={geofenceCircle} imageAlt='Geofence circular' />
    ),
  },
  {
    ...commonConfig,
    target: '#geofenceTour4 > button:nth-child(2)',
    placement: 'left-start',
    title: (
      <TitleTour
        title='Retangular: Defina o primeiro ponto a partir de um canto do retângulo.'
        description='Clique no mapa para marcar o primeiro canto do seu local e arraste até alcançar o tamanho desejado. Clique novamente no mapa para concluir o desenho.'
      />
    ),
    content: (
      <ContentTour image={geofenceRetangle} imageAlt='Geofence retangular.' />
    ),
  },
  {
    ...commonConfig,
    target: '#geofenceTour4 > button:nth-child(3)',
    placement: 'left-start',
    title: (
      <TitleTour
        title='Desenho: O desenho livre serve para desenhar polígonos.'
        description='É feito a partir de cliques no mapa, e sempre irá criar um formato fechado. Para iniciar o desenho, clique em um ponto no mapa, e siga clicando em demais pontos até criar o contorno da sua cerca. Finalize o desenho clicando no ponto inicial.'
      />
    ),
    content: (
      <ContentTour image={geofencePolygon} imageAlt='Geofence poligonal.' />
    ),
  },
  {
    ...commonConfig,
    target: '#geofenceTour5',
    placement: 'right-start',
    title: (
      <TitleTour
        title='Ferramentas de edição da cerca.'
        description='Todas as cercas criadas, podem ser editadas ou movimentadas para sem ajustadas na posição desejada. Para iniciar a edição ou a movimentação, clique nos controles no topo do mapa "Mover" ou "Editar".'
      />
    ),
    content: <ContentTour />,
  },
  {
    ...commonConfig,
    target: '#geofenceTour6',
    placement: 'right-start',
    title: (
      <TitleTour
        title='Movimentando a cerca.'
        description='Basta clicar em mover para ativar o modo de edição, em seguida clique na sua cerca e movimente até o local desejado, clique novamente para fixar no novo local. Para sair do modo de edição clique no botão "Fixar".'
      />
    ),
    content: <ContentTour image={geofenceDrag} imageAlt='Mover geofence' />,
  },
  {
    ...commonConfig,
    target: '#geofenceTour7',
    placement: 'right-start',
    title: (
      <TitleTour
        title='Editando cercas circular e retangular.'
        description='Para editar, clique no botão "editar" para ativar o modo de edição. Nos formatos circular e retangular você pode editar o tamanho das suas cercas.'
      />
    ),
    content: (
      <ContentTour
        image={geofenceCircleEdit}
        imageAlt='Editar geofence circular.'
      />
    ),
  },
  {
    ...commonConfig,
    target: '#geofenceTour7',
    placement: 'right-start',
    title: (
      <TitleTour
        title='Na edição do desenho você pode criar novos formatos no polígono.'
        description='Basta clicar nos pontos já criados e arrastar até o local desejado. Também é possível criar novos cantos no desenho clicando nos pontos menores. Ao finalizar clique em "finalizar edição".'
      />
    ),
    content: (
      <ContentTour
        image={geofencePolygonEdit}
        imageAlt='Editar geofence poligonal.'
      />
    ),
  },
  {
    ...commonConfig,
    target: '#geofenceTour8',
    placement: 'left-start',
    title: (
      <TitleTour title='Para reiniciar basta apagar o desenho e começar novamente.' />
    ),
    content: <ContentTour />,
  },
  {
    ...commonConfig,
    target: '#geofenceTour9',
    placement: 'left-start',
    title: (
      <TitleTour
        title='Por fim, defina o tipo de local.'
        description='O tipo de cerca pode ser utilizado para filtrar eventos de entrada e saída no relatório e fazer analises e controles dos seus locais.'
      />
    ),
    content: <ContentTour />,
  },
  {
    ...commonConfig,
    target: 'body',
    placement: 'center',
    floaterProps: {
      hideArrow: true,
    },
    title: (
      <TitleTour
        title='Atenção especial para cercas com 80 m² ou menos.'
        description='Locais com 80m² ou menos, podem não captar a entrada e saída dos veículos, caso o veículo esteja em velocidade alta.'
      />
    ),
    content: (
      <ContentTour
        image={geofenceAttention}
        imageAlt='Atenção especial para cercas com 80 m² ou menos.'
      />
    ),
  },
];

export default steps;
